import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Package, ArrowLeft, Plus, TrashSimple, TrendUp, TrendDown, Warning, CheckCircle, FloppyDisk,
  Scales,
} from "@phosphor-icons/react";

const canManageRoles = new Set(["yonetici"]);

function flattenLeaves(items, path = []) {
  const out = [];
  for (const it of items || []) {
    const p = [...path, it.name];
    if (it.children?.length > 0) {
      out.push(...flattenLeaves(it.children, p));
    } else {
      out.push({ ...it, breadcrumb: p.slice(0, -1).join(" › ") });
    }
  }
  return out;
}

// Recursive updater — leaf item bulup consumption_norms ekle/güncelle
function updateItemNorms(items, id, norms) {
  return items.map(it => {
    if (it.id === id) return { ...it, consumption_norms: norms };
    if (it.children?.length) return { ...it, children: updateItemNorms(it.children, id, norms) };
    return it;
  });
}

export default function BlockMaterialBudget() {
  const { siteId, blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = !!user;

  const [wbsItems, setWbsItems] = useState([]);
  const [meta, setMeta] = useState({ block_name: "", block_code: "" });
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [normDialog, setNormDialog] = useState(null); // {leaf}
  const [draftNorms, setDraftNorms] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [w, mb] = await Promise.all([
        axios.get(`${API}/blocks/${blockId}/wbs`),
        axios.get(`${API}/blocks/${blockId}/material-budget`),
      ]);
      setMeta({ block_name: w.data.block_name, block_code: w.data.block_code });
      setWbsItems(w.data.items || []);
      setBudget(mb.data);
      setDirty(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [blockId]);

  const leaves = useMemo(() => flattenLeaves(wbsItems), [wbsItems]);

  const openNorms = (leaf) => {
    setDraftNorms(JSON.parse(JSON.stringify(leaf.consumption_norms || [])));
    setNormDialog({ leaf });
  };
  const closeNorms = () => { setNormDialog(null); setDraftNorms([]); };
  const addDraftNorm = () => setDraftNorms([...draftNorms, { material_name: "", qty_per_unit: 0, unit: "" }]);
  const updateDraftNorm = (i, patch) => setDraftNorms(draftNorms.map((n, j) => i === j ? { ...n, ...patch } : n));
  const removeDraftNorm = (i) => setDraftNorms(draftNorms.filter((_, j) => j !== i));

  const applyNorms = () => {
    const clean = draftNorms.filter(n => n.material_name?.trim() && parseFloat(n.qty_per_unit) > 0)
                            .map(n => ({
                              material_name: n.material_name.trim(),
                              qty_per_unit: parseFloat(n.qty_per_unit),
                              unit: (n.unit || "").trim(),
                              notes: (n.notes || "").trim(),
                            }));
    setWbsItems(prev => updateItemNorms(prev, normDialog.leaf.id, clean));
    setDirty(true);
    closeNorms();
    toast.success(`${clean.length} norm hazır — Kaydet düğmesine basın`);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/blocks/${blockId}/wbs`, { items: wbsItems });
      toast.success("Sarfiyat normları kaydedildi");
      setDirty(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Shell
      title={meta.block_name ? `${meta.block_name} — Malzeme Bütçesi` : "Yükleniyor…"}
      subtitle="Proje İlerleme › Sarfiyat"
      hideSiteSelector
      actions={
        <Button variant="outline" onClick={() => navigate(`/proje/${siteId}/yapisi`)}
                className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
          <ArrowLeft size={14} className="mr-1.5" /> Yapıya Dön
        </Button>
      }
    >
      {/* Üst özet band */}
      {budget && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Blok</div>
            <div className="font-bold text-slate-900 truncate">{meta.block_name}</div>
            <div className="text-xs text-slate-500 font-mono">{meta.block_code || "—"}</div>
          </Card>
          <Card className="p-3 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Öngörülen Kalem</div>
            <div className="text-2xl font-bold text-[#1B4776] mt-1 font-heading" data-testid="mb-predicted-count">
              {budget.summary.predicted_items}
            </div>
          </Card>
          <Card className="p-3 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
              <TrendUp size={11} className="text-red-500" /> Fazla Gelen
            </div>
            <div className="text-2xl font-bold text-red-600 mt-1 font-heading" data-testid="mb-over">
              {budget.summary.over_delivered}
            </div>
          </Card>
          <Card className="p-3 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
              <TrendDown size={11} className="text-amber-500" /> Eksik Kalan
            </div>
            <div className="text-2xl font-bold text-amber-600 mt-1 font-heading" data-testid="mb-under">
              {budget.summary.under_delivered}
            </div>
          </Card>
        </div>
      )}

      {/* Karşılaştırma Tablosu */}
      <Card className="rounded-none sm:rounded-sm border-slate-200 mb-6">
        <div className="p-3 border-b border-slate-200 bg-slate-50">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
            <Scales size={16} weight="duotone" className="text-[#95C11F]" /> Öngörülen vs Sahaya Gelen
          </div>
        </div>
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-500">Yükleniyor…</div>
        ) : (budget?.rows || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 italic" data-testid="mb-empty">
            Henüz sarfiyat normu tanımlanmamış ve şantiyede malzeme yok.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2">Malzeme</th>
                  <th className="text-right px-3 py-2">Öngörülen</th>
                  <th className="text-right px-3 py-2">Sahaya Gelen</th>
                  <th className="text-right px-3 py-2">Sapma</th>
                  <th className="text-right px-3 py-2">%</th>
                  <th className="text-center px-3 py-2">Durum</th>
                </tr>
              </thead>
              <tbody>
                {(budget?.rows || []).map((r, i) => {
                  const variance = r.variance || 0;
                  const isOver = variance > 0;
                  const isUnder = variance < 0 && r.has_prediction;
                  const isOk = Math.abs(variance) < 0.001 && r.has_prediction && r.has_receipt;
                  return (
                    <tr key={i} data-testid={`mb-row-${i}`}
                        className={`border-b border-slate-100 ${!r.has_prediction ? "bg-slate-50/50" : ""}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.material_name}</div>
                        {!r.has_prediction && (
                          <div className="text-[9px] uppercase tracking-widest text-slate-400">Norm dışı</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {r.has_prediction ? (
                          <>{r.predicted_qty.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                            <span className="text-slate-400 ml-1">{r.unit}</span></>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {r.has_receipt ? (
                          <>{r.received_qty.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                            <span className="text-slate-400 ml-1">{r.unit}</span>
                            <span className="text-[9px] text-slate-400 ml-1">({r.entries_count})</span></>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${
                        isOver ? "text-red-600" : isUnder ? "text-amber-600" : "text-slate-500"
                      }`}>
                        {variance !== 0 && (variance > 0 ? "+" : "")}
                        {variance.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${
                        isOver ? "text-red-600" : isUnder ? "text-amber-600" : "text-slate-500"
                      }`}>
                        {r.variance_percent !== null && r.variance_percent !== undefined
                          ? `${r.variance_percent > 0 ? "+" : ""}${r.variance_percent.toFixed(1)}%`
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isOk && <CheckCircle size={14} weight="fill" className="text-emerald-500 inline" />}
                        {isOver && <Warning size={14} weight="fill" className="text-red-500 inline" title="Öngörüden fazla" />}
                        {isUnder && <Warning size={14} weight="fill" className="text-amber-500 inline" title="Öngörüden az" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Norm Tanımlama Bölümü */}
      <Card className="rounded-none sm:rounded-sm border-slate-200">
        <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
            <Package size={16} weight="duotone" className="text-[#95C11F]" /> Sarfiyat Normları (Blok WBS Kalemlerine)
          </div>
          {canManage && dirty && (
            <Button onClick={save} disabled={saving}
                    data-testid="mb-save"
                    size="sm"
                    className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] font-bold">
              <FloppyDisk size={12} className="mr-1" />
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          )}
        </div>
        <div className="p-3">
          {leaves.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500 italic">
              Bu blokta WBS kalemi yok. Önce WBS tanımlayın.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-2 py-2">Kod</th>
                  <th className="text-left px-2 py-2">İmalat</th>
                  <th className="text-right px-2 py-2">Planlanan</th>
                  <th className="text-center px-2 py-2">Norm</th>
                  {canManage && <th className="text-center px-2 py-2 w-24">İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {leaves.map(l => (
                  <tr key={l.id} data-testid={`mb-leaf-${l.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-2 font-mono text-xs text-slate-500">{l.code}</td>
                    <td className="px-2 py-2">
                      <div className="font-medium text-slate-900 text-xs">{l.name}</div>
                      {l.breadcrumb && <div className="text-[10px] text-slate-400 truncate">{l.breadcrumb}</div>}
                    </td>
                    <td className="px-2 py-2 text-right text-xs font-mono">
                      {(l.planned_qty || 0).toLocaleString("tr-TR")} <span className="text-slate-400">{l.unit}</span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {(l.consumption_norms || []).length === 0 ? (
                        <span className="text-xs text-slate-400 italic">tanımlı değil</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(l.consumption_norms || []).map((n, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-[#F7FBE9] border border-[#95C11F]/40 text-[#1B4776] font-mono"
                                  title={`${n.material_name}: ${n.qty_per_unit} ${n.unit}/${l.unit || 'br'}`}>
                              {n.material_name}: {n.qty_per_unit}{n.unit}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-2 py-2 text-center">
                        <Button onClick={() => openNorms(l)} size="sm" variant="outline"
                                data-testid={`mb-norms-btn-${l.id}`}
                                className="rounded-none sm:rounded-sm h-7 text-[10px] uppercase tracking-widest">
                          {(l.consumption_norms || []).length > 0 ? "Düzenle" : "Ekle"}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Norm Ekle/Düzenle Dialog */}
      <Dialog open={!!normDialog} onOpenChange={(v) => !v && closeNorms()}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto"
                       data-testid="mb-norm-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">
              Sarfiyat Normu — {normDialog?.leaf?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Bir birim <b>{normDialog?.leaf?.unit || "iş"}</b> için hangi malzemeden ne kadar sarf edileceğini girin.
              Örn: 1 m³ beton için 350 kg çimento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {draftNorms.length === 0 && (
              <div className="text-sm text-slate-500 italic text-center py-6">Henüz norm eklenmedi</div>
            )}
            {draftNorms.map((n, i) => (
              <div key={i} className="grid grid-cols-[2fr,1fr,1fr,32px] gap-2 items-end"
                   data-testid={`mb-draft-${i}`}>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 block">Malzeme Adı</Label>}
                  <Input value={n.material_name}
                         data-testid={`mb-draft-name-${i}`}
                         onChange={e => updateDraftNorm(i, { material_name: e.target.value })}
                         placeholder="Örn: Çimento"
                         className="h-8 rounded-none sm:rounded-sm" />
                </div>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 block">Birim İş Başına</Label>}
                  <Input type="number" min="0" step="0.01"
                         value={n.qty_per_unit || ""}
                         data-testid={`mb-draft-qty-${i}`}
                         onChange={e => updateDraftNorm(i, { qty_per_unit: e.target.value })}
                         placeholder="0"
                         className="h-8 rounded-none sm:rounded-sm text-right font-mono" />
                </div>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 block">Birim</Label>}
                  <Input value={n.unit}
                         data-testid={`mb-draft-unit-${i}`}
                         onChange={e => updateDraftNorm(i, { unit: e.target.value })}
                         placeholder="kg"
                         className="h-8 rounded-none sm:rounded-sm font-mono" />
                </div>
                <div>
                  <button onClick={() => removeDraftNorm(i)}
                          data-testid={`mb-draft-del-${i}`}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50">
                    <TrashSimple size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-2">
            <Button onClick={addDraftNorm} variant="outline"
                    data-testid="mb-draft-add"
                    className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest">
              <Plus size={12} className="mr-1" /> Yeni Norm
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeNorms}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={applyNorms}
                    data-testid="mb-norm-apply"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              Uygula
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
